import * as Dialog from "@radix-ui/react-dialog"
import type { GalleryImage, ScrollGalleryItem, ScrollGalleryModalProps } from "@site-types/workGallery"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"
import OptimizedImage from "../../OptimizedImage"

const PARALLAX_FACTOR = 20
const MIN_SLIDE_OPACITY = 0.38

function imageIndexesToLoad(selectedIndex: number, imageCount: number) {
  if (imageCount <= 0) return new Set<number>()

  return new Set([
    selectedIndex,
    (selectedIndex - 1 + imageCount) % imageCount,
    (selectedIndex + 1) % imageCount,
  ])
}

function adjacentImageIndexes(selectedIndex: number, imageCount: number) {
  if (imageCount <= 1) return []

  return [...new Set([(selectedIndex - 1 + imageCount) % imageCount, (selectedIndex + 1) % imageCount])]
}

function WorkGalleryDialogContent({ images }: { images: GalleryImage[] }) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [loadedImageSources, setLoadedImageSources] = useState<Set<string>>(() => new Set())
  const [modalViewportRef, modalEmblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
  })

  const canNavigate = images.length > 1
  const loadableImageIndexes = imageIndexesToLoad(selectedIndex, images.length)

  const syncSelectedIndex = useCallback(() => {
    if (!modalEmblaApi) return
    setSelectedIndex(modalEmblaApi.selectedScrollSnap())
  }, [modalEmblaApi])

  const markImageLoaded = useCallback((src: string) => {
    setLoadedImageSources((currentSources) => {
      if (currentSources.has(src)) return currentSources

      const nextSources = new Set(currentSources)
      nextSources.add(src)
      return nextSources
    })
  }, [])

  const tweenSlides = useCallback(
    (eventName?: "scroll") => {
      if (!modalEmblaApi) return

      const engine = modalEmblaApi.internalEngine()
      const scrollProgress = modalEmblaApi.scrollProgress()
      const scrollSnaps = modalEmblaApi.scrollSnapList()
      const slidesInView = modalEmblaApi.slidesInView()
      const slideNodes = modalEmblaApi.slideNodes()

      scrollSnaps.forEach((scrollSnap, snapIndex) => {
        const slidesInSnap = engine.slideRegistry[snapIndex] ?? [snapIndex]

        slidesInSnap.forEach((slideIndex) => {
          const slide = slideNodes[slideIndex]
          const parallaxLayer = slide?.querySelector<HTMLElement>("[data-gallery-parallax]")

          if (!slide || !parallaxLayer) return
          if (eventName === "scroll" && !slidesInView.includes(slideIndex)) return

          let diffToTarget = scrollSnap - scrollProgress

          if (engine.options.loop) {
            for (const loopItem of engine.slideLooper.loopPoints) {
              const target = loopItem.target()

              if (slideIndex === loopItem.index && target !== 0) {
                diffToTarget =
                  target < 0 ? scrollSnap - (1 + scrollProgress) : scrollSnap + (1 - scrollProgress)
              }
            }
          }

          const tweenValue = diffToTarget * -PARALLAX_FACTOR
          const opacity = Math.max(MIN_SLIDE_OPACITY, 1 - Math.abs(diffToTarget * 2.4))

          slide.style.opacity = String(opacity)
          parallaxLayer.style.transform = `translateX(${tweenValue}%)`
        })
      })
    },
    [modalEmblaApi]
  )

  useEffect(() => {
    if (!modalEmblaApi) return

    const sync = () => {
      syncSelectedIndex()
      tweenSlides()
    }
    const syncScroll = () => {
      tweenSlides("scroll")
    }

    modalEmblaApi.on("scroll", syncScroll)
    modalEmblaApi.on("select", sync)
    modalEmblaApi.on("reInit", sync)
    syncSelectedIndex()
    tweenSlides()

    return () => {
      modalEmblaApi.off("scroll", syncScroll)
      modalEmblaApi.off("select", sync)
      modalEmblaApi.off("reInit", sync)
    }
  }, [modalEmblaApi, syncSelectedIndex, tweenSlides])

  useEffect(() => {
    if (!modalEmblaApi) return

    modalEmblaApi.reInit()
    modalEmblaApi.scrollTo(0, true)
    setSelectedIndex(0)
    tweenSlides()
  }, [modalEmblaApi, tweenSlides])

  useEffect(() => {
    setLoadedImageSources(new Set())
  }, [images])

  useEffect(() => {
    if (typeof Image === "undefined") return

    for (const imageIndex of adjacentImageIndexes(selectedIndex, images.length)) {
      const src = images[imageIndex]?.src
      if (!src || loadedImageSources.has(src)) continue

      const image = new Image()
      image.src = src
    }
  }, [images, loadedImageSources, selectedIndex])

  const scrollPrev = () => {
    if (canNavigate) modalEmblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    if (canNavigate) modalEmblaApi?.scrollNext()
  }

  return (
    <Dialog.Content
      data-id="work-gallery-dialog-content"
      className="fixed top-1/2 left-1/2 z-[101] grid max-h-[88vh] w-[min(94vw,1280px)] -translate-x-1/2 -translate-y-1/2 place-items-center outline-none [animation:work-gallery-fade-in_0.15s_ease] max-modal:w-[92vw]"
      aria-label="Image gallery"
      aria-describedby={undefined}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") scrollPrev()
        if (event.key === "ArrowRight") scrollNext()
      }}
    >
      <Dialog.Title className="absolute m-[-1px] h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0_0_0_0)]">
        Image gallery
      </Dialog.Title>
      <Dialog.Close asChild>
        <button
          data-id="work-gallery-close-content"
          className="absolute top-[1.5rem] right-[1.5rem] z-[2] grid cursor-pointer place-items-center border-0 bg-transparent p-0 text-[24px] text-[var(--white)] leading-none max-modal:hidden max-modal:h-[44px] max-modal:w-[44px] max-modal:text-[1.45rem]"
          type="button"
          aria-label="Close gallery"
        >
          <i className="fa-sharp fa-regular fa-xmark" />
        </button>
      </Dialog.Close>

      <div
        data-id="work-gallery-dialog-viewport"
        className="w-full overflow-hidden leading-[0] [--modal-slide-size:72%] [--slide-shadow-space:18px] [--slide-spacing:2rem] max-modal:[--modal-slide-size:82%] max-modal:[--slide-spacing:1.4rem]"
        ref={modalViewportRef}
      >
        <div
          data-id="work-gallery-dialog-container"
          className="ml-[calc(var(--slide-spacing)*-1)] flex [touch-action:pan-y_pinch-zoom]"
        >
          {images.map((image, index) => {
            const isLoadable = loadableImageIndexes.has(index)
            const isLoaded = loadedImageSources.has(image.src)

            return (
              <div
                className="min-w-0 flex-[0_0_var(--modal-slide-size)] pr-[var(--slide-shadow-space)] pb-[var(--slide-shadow-space)] pl-[var(--slide-spacing)] opacity-100 transition-opacity duration-[160ms] ease-out will-change-[opacity]"
                data-testid="marketing-gallery-slide"
                key={image.src}
              >
                <figure
                  className="data-[loaded=true]:before:animation-none relative m-0 aspect-video overflow-hidden border-4 border-[var(--white)] bg-[radial-gradient(circle_at_20%_0%,rgba(34,88,170,0.24),transparent_36%),linear-gradient(135deg,#071021_0%,#02050b_100%)] shadow-[10px_10px_0_var(--blue-50)] before:absolute before:inset-0 before:-translate-x-full before:bg-[linear-gradient(110deg,transparent_0%,rgba(255,255,255,0.06)_42%,rgba(255,255,255,0.14)_50%,rgba(255,255,255,0.06)_58%,transparent_100%)] before:content-[''] data-[loaded=true]:before:opacity-0 before:[animation:work-gallery-shimmer_1.25s_ease-in-out_infinite]"
                  data-loaded={isLoaded ? "true" : undefined}
                  data-testid="marketing-gallery-frame"
                >
                  <div
                    className="absolute inset-0 z-[1] transition-transform duration-[120ms] ease-out will-change-transform"
                    data-gallery-parallax
                    data-testid="marketing-gallery-parallax-layer"
                  >
                    <OptimizedImage
                      className="block h-full w-full object-contain opacity-100 transition-opacity duration-[180ms] ease-out"
                      src={isLoadable || isLoaded ? image.src : undefined}
                      data-src={image.src}
                      alt={image.alt}
                      title={image.title}
                      loading={isLoadable ? "eager" : "lazy"}
                      decoding="async"
                      onLoad={() => markImageLoaded(image.src)}
                      {...image.dimensions}
                    />
                  </div>
                </figure>
              </div>
            )
          })}
        </div>
      </div>

      <div
        data-id="work-gallery-dialog-controls"
        className="contents max-modal:mt-[0.85rem] max-modal:flex max-modal:items-center max-modal:justify-center max-modal:gap-[1rem]"
      >
        <button
          data-id="work-gallery-dialog-prev"
          className="absolute top-1/2 left-[-4.5rem] z-[2] grid h-[8rem] w-[4rem] -translate-y-1/2 cursor-pointer place-items-center border-0 bg-transparent text-[3rem] text-[var(--white)] leading-none enabled:opacity-72 enabled:hover:opacity-100 disabled:pointer-events-none disabled:opacity-0 max-modal:static max-modal:h-[44px] max-modal:w-[44px] max-modal:translate-y-0 max-modal:text-[1.45rem]"
          type="button"
          onClick={scrollPrev}
          disabled={!canNavigate}
          aria-label="Previous image"
        >
          <i className="fa-sharp fa-solid fa-chevron-left" />
        </button>

        <div
          className="mt-[1.2rem] text-[1.4rem] text-[var(--white)] leading-none max-modal:mt-0 max-modal:min-w-[4.5rem] max-modal:text-center max-modal:text-[1rem]"
          data-testid="marketing-gallery-status"
        >
          {selectedIndex + 1} / {images.length}
        </div>

        <button
          data-id="work-gallery-dialog-next"
          className="absolute top-1/2 right-[-4.5rem] z-[2] grid h-[8rem] w-[4rem] -translate-y-1/2 cursor-pointer place-items-center border-0 bg-transparent text-[3rem] text-[var(--white)] leading-none enabled:opacity-72 enabled:hover:opacity-100 disabled:pointer-events-none disabled:opacity-0 max-modal:static max-modal:h-[44px] max-modal:w-[44px] max-modal:translate-y-0 max-modal:text-[1.45rem]"
          type="button"
          onClick={scrollNext}
          disabled={!canNavigate}
          aria-label="Next image"
        >
          <i className="fa-sharp fa-solid fa-chevron-right" />
        </button>
      </div>
    </Dialog.Content>
  )
}

export const ScrollGalleryModal = ({ items, onOpenChange, openIndex }: ScrollGalleryModalProps) => {
  const currentItem = openIndex !== null ? items[openIndex] : null

  return (
    <Dialog.Root
      open={openIndex !== null}
      onOpenChange={onOpenChange}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-[rgba(0,0,0,0.88)] [-webkit-backdrop-filter:blur(6px)] [animation:work-gallery-fade-in_0.15s_ease] [backdrop-filter:blur(6px)]" />
        <Dialog.Close asChild>
          <button
            data-id="work-gallery-close-viewport"
            className="hidden cursor-pointer place-items-center border-0 bg-transparent text-[var(--white)] leading-none max-modal:fixed max-modal:top-[max(12px,env(safe-area-inset-top))] max-modal:right-[max(12px,env(safe-area-inset-right))] max-modal:z-[102] max-modal:grid max-modal:h-auto max-modal:w-auto max-modal:p-0 max-modal:text-[1.45rem]"
            type="button"
            aria-label="Close gallery"
          >
            <i className="fa-sharp fa-regular fa-xmark" />
          </button>
        </Dialog.Close>
        {currentItem && <WorkGalleryDialogContent images={currentItem.images} />}
      </Dialog.Portal>
    </Dialog.Root>
  )
}
